/**
 * Visible_Text_Length 的唯一实现。
 *
 * 需求 Glossary 把 Visible_Text_Length 定义成一条固定的流水线，本模块逐步照做：
 *   1. 移除 `<script>` / `<style>` / `<noscript>` 元素**及其内容**
 *   2. 移除全部 HTML 标签（注释、DOCTYPE 等非文本结构同样不算可见文本）
 *   3. 解码 HTML 实体
 *   4. 连续空白折叠为单个空格
 *   5. 去除首尾空白
 *   6. 取字符数（中文按 1 字符计，按 **code point** 计数，避免代理对被算作 2）
 *
 * 这个口径同时被三处复用，所以必须是不依赖 DOM 的纯函数（要能在 Node 里跑）：
 * - `scripts/prerender/selfCheck.ts`：构建期对 `out.staging` 产物自检（需求 10.3-10.6）
 * - `scripts/geo-inspect/checks.ts`：线上巡检对 Raw_HTML 逐项校验（需求 12.1）
 * - 页面侧的内容下限判断读 `PAGE_TYPE_META_PRESET[pageType].bounds.visibleTextMin`
 *
 * 「生成期口径 == 巡检期口径」正是靠共用这一份实现保证的，
 * 因此这里不使用 `DOMParser`、`document` 或任何浏览器 API。
 *
 * 对应需求：10.3、10.4、10.5、10.6、12.1
 */

/**
 * Raw text 元素：内容不是标记而是原始文本，且都不是可见文本。
 *
 * 正则同时接受「有闭合标签」与「到输入结尾都没闭合」两种情况：
 * 未闭合时按浏览器行为一路吃到结尾，保证「script/style/noscript 内的内容
 * 永不计入」这条性质对残缺 HTML 也成立。
 */
const RAW_TEXT_ELEMENT_PATTERN = /<(script|style|noscript)\b[^>]*>[\s\S]*?(?:<\/\1[^>]*>|$)/gi;

/** HTML 实体：命名实体、十进制字符引用、十六进制字符引用 */
const ENTITY_PATTERN = /&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;

/** 连续空白（JS 的 `\s` 已含 `\u00a0` 与全角空格 `\u3000`） */
const WHITESPACE_RUN_PATTERN = /\s+/g;

/**
 * 命名实体表。
 *
 * 只收录站点文案与富文本编辑器实际会产出的实体：字符数计量对个别生僻实体
 * 是否解码并不敏感（未收录的实体保持原样，按其字面字符数计入），
 * 与其引入一份几千条的完整表，不如让这张表小到可以一眼校对。
 */
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: "\u00a0",
  ensp: "\u2002",
  emsp: "\u2003",
  thinsp: "\u2009",
  zwnj: "\u200c",
  zwj: "\u200d",
  shy: "\u00ad",
  ndash: "\u2013",
  mdash: "\u2014",
  horbar: "\u2015",
  hellip: "\u2026",
  bull: "\u2022",
  middot: "\u00b7",
  lsquo: "\u2018",
  rsquo: "\u2019",
  ldquo: "\u201c",
  rdquo: "\u201d",
  sbquo: "\u201a",
  bdquo: "\u201e",
  laquo: "\u00ab",
  raquo: "\u00bb",
  lsaquo: "\u2039",
  rsaquo: "\u203a",
  dagger: "\u2020",
  Dagger: "\u2021",
  permil: "\u2030",
  prime: "\u2032",
  Prime: "\u2033",
  copy: "\u00a9",
  reg: "\u00ae",
  trade: "\u2122",
  deg: "\u00b0",
  plusmn: "\u00b1",
  times: "\u00d7",
  divide: "\u00f7",
  minus: "\u2212",
  ne: "\u2260",
  le: "\u2264",
  ge: "\u2265",
  asymp: "\u2248",
  infin: "\u221e",
  sup2: "\u00b2",
  sup3: "\u00b3",
  frac12: "\u00bd",
  frac14: "\u00bc",
  frac34: "\u00be",
  micro: "\u00b5",
  para: "\u00b6",
  sect: "\u00a7",
  euro: "\u20ac",
  pound: "\u00a3",
  yen: "\u00a5",
  cent: "\u00a2",
  curren: "\u00a4",
  larr: "\u2190",
  uarr: "\u2191",
  rarr: "\u2192",
  darr: "\u2193",
  harr: "\u2194",
  check: "\u2713",
  star: "\u2606",
  starf: "\u2605",
};

/** Unicode 替换字符，用于非法字符引用（与 HTML 规范一致） */
const REPLACEMENT_CHARACTER = "\ufffd";

/** 标签名首字符：ASCII 字母。其他字符跟在 `<` 后面时，`<` 是普通文本 */
function isTagNameStart(char: string): boolean {
  return (char >= "a" && char <= "z") || (char >= "A" && char <= "Z");
}

function isWhitespace(char: string): boolean {
  return char === " " || char === "\t" || char === "\n" || char === "\r" || char === "\f";
}

/**
 * 从标签名起始位置扫描到该标签的结束 `>`，找不到返回 -1。
 *
 * 引号只在「紧跟 `=`（可含空白）」时才被视为属性值定界符，
 * 这样 `<a title="a>b">` 是一个完整标签，而 `<a href=a'b>` 里的 `'`
 * 不会把后面的 `>` 吃掉。
 */
function findTagEnd(html: string, start: number): number {
  let index = start;
  let afterEquals = false;

  while (index < html.length) {
    const char = html[index];

    if (char === ">") {
      return index;
    }
    if (char === "=") {
      afterEquals = true;
      index += 1;
      continue;
    }
    if (isWhitespace(char)) {
      index += 1;
      continue;
    }
    if (afterEquals && (char === '"' || char === "'")) {
      const closingQuote = html.indexOf(char, index + 1);
      if (closingQuote < 0) {
        return -1;
      }
      index = closingQuote + 1;
      afterEquals = false;
      continue;
    }
    afterEquals = false;
    index += 1;
  }

  return -1;
}

/**
 * 移除全部标签、注释与 DOCTYPE 之类的非文本结构，只留文本节点内容。
 *
 * 未闭合的普通标签（到输入结尾都没有 `>`）按字面文本保留：
 * 这样「往片段末尾追加可见文本」不会让计量结果变小
 * （`abc<` 计 4，追加 `d` 后 `abc<d` 计 5），
 * 需求 10.3-10.6 关心的是「不少于下限」，单调性比模拟浏览器的容错更重要。
 *
 * 未闭合的注释 / DOCTYPE / 处理指令按同一条规则处理（同样保留为字面文本）。
 * 早先的写法是「未闭合就吃到结尾」，属性测试给出了反例：`<` 单独出现时按
 * 字面文本计 1，追加可见文本 `!` 得到 `<!`，若按未闭合声明吃到结尾就变成 0，
 * 破坏了本函数明确要保证的单调性。良构 HTML 里这三种结构总是闭合的，
 * 因此这个改动只影响残缺输入。
 */
function stripTags(html: string): string {
  let text = "";
  let index = 0;

  while (index < html.length) {
    const tagStart = html.indexOf("<", index);
    if (tagStart < 0) {
      text += html.slice(index);
      break;
    }
    text += html.slice(index, tagStart);

    // 结尾孤立的 `<`：按字面文本处理
    if (tagStart === html.length - 1) {
      text += "<";
      break;
    }

    // HTML 注释：闭合时内容不可见；未闭合时按字面文本保留（保单调性）
    if (html.startsWith("<!--", tagStart)) {
      const commentEnd = html.indexOf("-->", tagStart + 4);
      if (commentEnd < 0) {
        text += html.slice(tagStart);
        break;
      }
      index = commentEnd + 3;
      continue;
    }

    const nextChar = html[tagStart + 1];

    // DOCTYPE、CDATA、处理指令：闭合时同样不是可见文本；未闭合时按字面文本保留
    if (nextChar === "!" || nextChar === "?") {
      const declarationEnd = html.indexOf(">", tagStart + 2);
      if (declarationEnd < 0) {
        text += html.slice(tagStart);
        break;
      }
      index = declarationEnd + 1;
      continue;
    }

    // `<` 后面既不是标签名也不是 `/`，它就是一个普通的小于号
    if (nextChar !== "/" && !isTagNameStart(nextChar)) {
      text += "<";
      index = tagStart + 1;
      continue;
    }

    const tagEnd = findTagEnd(html, tagStart + 1);
    if (tagEnd < 0) {
      text += html.slice(tagStart);
      break;
    }
    index = tagEnd + 1;
  }

  return text;
}

/** 解析 `&#123;` / `&#x1f600;` 形式的字符引用，非法码位返回 U+FFFD */
function decodeNumericReference(body: string): string {
  const isHex = body[1] === "x" || body[1] === "X";
  const digits = isHex ? body.slice(2) : body.slice(1);
  const codePoint = Number.parseInt(digits, isHex ? 16 : 10);

  if (!Number.isFinite(codePoint)) {
    return REPLACEMENT_CHARACTER;
  }
  // 0、代理对区间与超出 Unicode 上限的码位都不是合法字符
  if (codePoint === 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
    return REPLACEMENT_CHARACTER;
  }
  return String.fromCodePoint(codePoint);
}

/**
 * 解码 HTML 实体，**只解一遍**。
 *
 * 单遍替换让 `&amp;lt;` 解出 `&lt;` 而不是 `<`，也保证解码结果不会被
 * 重新当成标签处理（流水线里解码永远发生在去标签之后）。
 * 未收录的命名实体原样保留。
 */
export function decodeHtmlEntities(html: string): string {
  if (!html || html.indexOf("&") < 0) {
    return html ?? "";
  }

  return html.replace(ENTITY_PATTERN, (match, body: string) => {
    if (body.startsWith("#")) {
      return decodeNumericReference(body);
    }
    const exact = NAMED_ENTITIES[body];
    if (exact !== undefined) {
      return exact;
    }
    const lowercased = NAMED_ENTITIES[body.toLowerCase()];
    return lowercased !== undefined ? lowercased : match;
  });
}

/**
 * 按 Glossary 定义抽取 Raw_HTML 的可见文本：
 * 去 raw text 元素 → 去标签 → 解码实体 → 折叠空白 → 去首尾空白。
 *
 * `computeVisibleTextLength()` 的中间结果，单独导出便于自检与巡检在报告里
 * 打印实际文本片段辅助定位。
 */
export function extractVisibleText(html: string): string {
  if (!html) {
    return "";
  }

  const withoutRawText = html.replace(RAW_TEXT_ELEMENT_PATTERN, "");
  const withoutTags = stripTags(withoutRawText);
  const decoded = decodeHtmlEntities(withoutTags);

  return decoded.replace(WHITESPACE_RUN_PATTERN, " ").trim();
}

/**
 * Visible_Text_Length：Raw_HTML 的可见文本字符数。
 *
 * 用 code point 计数而不是 `String.prototype.length`：后者对 BMP 外字符
 * （表情符号等）会算成 2，会让页面看起来「文本更多」。中文按 1 字符计。
 *
 * @param html Raw_HTML 文本，允许是空串
 * @returns 可见文本的 code point 数量，非字符串输入返回 0
 *
 * 对应需求：10.3、10.4、10.5、10.6、12.1
 */
export function computeVisibleTextLength(html: string): number {
  if (typeof html !== "string" || html.length === 0) {
    return 0;
  }

  const visibleText = extractVisibleText(html);
  if (visibleText.length === 0) {
    return 0;
  }

  // `Array.from` 按 code point 迭代字符串，代理对被当成 1 个字符
  return Array.from(visibleText).length;
}
